import {
  type Collision,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DropAnimation,
  MouseSensor,
  TouchSensor,
  useDndContext,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, type SortingStrategy, useSortable } from '@dnd-kit/sortable'
import type { RowId } from '@eformulieren/model'
import { type ReactNode, useEffect, useState } from 'react'

/**
 * Een rij van een herhaalgroep naar een andere plek slepen (R-40, R-139), met hetzelfde patroon als
 * het canvas van de configurator (ontwerp 8.2, "Slepen (besluit van 30 oktober 2026)";
 * `apps/configurator/src/app/canvas/draggable.tsx` en `insertion.ts`).
 *
 * Wat daarvan hier terugkomt, en waarom het niet wordt geïmporteerd: de renderer hangt niet aan de
 * configurator. Het patroon is klein genoeg om het hier opnieuw te schrijven voor één lijst die
 * alleen boven en onder kent.
 *
 * - **Niets schuift opzij tijdens het slepen** ({@link stillStrategy}); de oorspronkelijke rij blijft
 *   gedempt staan en een klein kaartje met de naam volgt de pointer.
 * - **Een lijn met een stip toont waar de rij landt**, midden in de ruimte tussen twee rijen en zonder
 *   zelf ruimte te nemen: boven of onder de rij die het dichtst bij de pointer ligt, naar de helft van
 *   die rij waar de pointer staat ({@link rowCollision}). De lijn en de uitkomst komen uit dezelfde
 *   botsing; een plek waar de rij al staat krijgt geen lijn, want daar gebeurt niets.
 * - **Na een geslaagde plaatsing vliegt het kaartje niet terug**; de rij licht op haar nieuwe plek
 *   kort op. Naast een doel loslaten of Escape veert het kaartje terug: er is niets gebeurd.
 * - **Rustig automatisch schuiven**: pas in de buitenste 6 procent van de schermhoogte, hooguit half zo
 *   snel als de standaard.
 * - **Met een vinger na even vasthouden** (200 milliseconden), zodat een veeg over het formulier blijft
 *   scrollen.
 *
 * **De greep is geen knop en zit niet in de tabvolgorde.** Omhoog en Omlaag in de kop van de rij zijn
 * de weg zonder slepen (WCAG 2.5.7) en met het toetsenbord (WCAG 2.1.1), en ze roepen dezelfde
 * verplaatsing aan als het loslaten hier (`moveRowTo` in `sectionDocument.ts`). Een greep die met
 * Tab bereikbaar is en met het toetsenbord niets doet, zou een lege stop zijn.
 */

export type Side = 'voor' | 'na'

/** Niets opschuiven tijdens het slepen; zie de kop. */
export const stillStrategy: SortingStrategy = () => null

const CALM_AUTO_SCROLL = { threshold: { x: 0, y: 0.06 }, acceleration: 5 } as const

/** Afstand van een punt tot een rechthoek; nul als het punt erin ligt. */
function distance(x: number, y: number, rect: DOMRect): number {
  const dx = Math.max(rect.left - x, 0, x - rect.right)
  const dy = Math.max(rect.top - y, 0, y - rect.bottom)
  return Math.hypot(dx, dy)
}

/**
 * De rij die het dichtst bij de pointer ligt, met de kant waar hij landt: de helft van die rij waar
 * de pointer staat (het patroon "closest edge" van Atlassian, zoals `insertion.ts` van het canvas).
 * Gemeten op het moment zelf, want de pagina kan intussen zijn geschoven.
 */
const rowCollision: CollisionDetection = ({ collisionRect, droppableContainers, pointerCoordinates }) => {
  const point = pointerCoordinates ?? { x: collisionRect.left + collisionRect.width / 2, y: collisionRect.top + collisionRect.height / 2 }
  let best: { id: string | number; rect: DOMRect; d: number } | undefined
  for (const container of droppableContainers) {
    const node = container.node.current
    if (container.disabled || node === null || !node.isConnected) continue
    const rect = node.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) continue
    const d = distance(point.x, point.y, rect)
    if (best === undefined || d < best.d) best = { id: container.id, rect, d }
  }
  if (best === undefined) return []
  const kant: Side = point.y < best.rect.top + best.rect.height / 2 ? 'voor' : 'na'
  const collision: Collision = { id: best.id, data: { kant, value: best.d } }
  return [collision]
}

interface Insertion {
  readonly id: string
  readonly kant: Side
}

function insertionOf(collisions: readonly Collision[] | null | undefined): Insertion | undefined {
  const first = collisions?.[0]
  const kant = (first?.data as { kant?: Side } | undefined)?.kant
  return first === undefined || kant === undefined ? undefined : { id: String(first.id), kant }
}

/**
 * Waar de gesleepte rij landt, als rij-id om vóór te zetten (`undefined` is het eind), of `null` als
 * er niets verandert: op zichzelf, of op de plek waar hij al staat.
 */
function landingOf(order: readonly RowId[], source: RowId, insertion: Insertion | undefined): RowId | undefined | null {
  if (insertion === undefined || insertion.id === source) return null
  const target = insertion.id as RowId
  const rest = order.filter((id) => id !== source)
  const at = rest.indexOf(target)
  if (at < 0) return null
  const before = insertion.kant === 'voor' ? target : rest[at + 1]
  const now = order[order.indexOf(source) + 1]
  return before === now ? null : before
}

export interface RowDragAreaProps {
  readonly rowIds: readonly RowId[]
  /** De naam op het kaartje bij de pointer. */
  readonly nameOf: (rowId: RowId) => string
  /** Verplaatst de rij; `undefined` is naar het eind. Geeft terug of er iets veranderde. */
  readonly onMove: (rowId: RowId, before: RowId | undefined) => boolean
  readonly children: ReactNode
}

/** De sleeplaag om de rijen van één herhaalgroep. */
export function RowDragArea({ rowIds, nameOf, onMove, children }: RowDragAreaProps): ReactNode {
  const sensors = useSensors(
    // Een afstand vóór het slepen begint, zodat een klik op de greep geen sleepbeweging wordt.
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  )
  const [active, setActive] = useState<RowId | undefined>(undefined)
  const [dropAnimation, setDropAnimation] = useState<DropAnimation | null | undefined>(undefined)
  const [landed, setLanded] = useState<{ readonly id: RowId } | undefined>(undefined)

  function end(event: DragEndEvent): void {
    setActive(undefined)
    const source = String(event.active.id) as RowId
    const before = landingOf(rowIds, source, insertionOf(event.collisions))
    if (before === null) return
    if (!onMove(source, before)) return
    setDropAnimation(null)
    setLanded({ id: source })
  }

  useEffect(() => {
    if (landed === undefined) return
    const element = document.querySelector(`[data-rij="${landed.id}"]`)
    if (element === null) return
    element.setAttribute('data-geland', 'ja')
    // Even lang als de animatie in schil.css; bij `prefers-reduced-motion` is het attribuut een stille markering.
    const timer = setTimeout(() => element.removeAttribute('data-geland'), 800)
    return () => {
      clearTimeout(timer)
      element.removeAttribute('data-geland')
    }
  }, [landed])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rowCollision}
      autoScroll={CALM_AUTO_SCROLL}
      // De schil meldt zelf waar de rij staat na het loslaten (`RepeatGroup`), in de taal van de lezer;
      // de Engelse meldingen van dnd-kit staan uit.
      accessibility={{ announcements: SILENT, screenReaderInstructions: { draggable: '' } }}
      onDragStart={(event) => {
        setDropAnimation(undefined)
        setActive(String(event.active.id) as RowId)
      }}
      onDragEnd={end}
      onDragCancel={() => setActive(undefined)}
    >
      <SortableContext items={rowIds as RowId[]} strategy={stillStrategy}>
        {children}
      </SortableContext>
      <DragOverlay {...(dropAnimation === undefined ? {} : { dropAnimation })}>
        {active === undefined ? null : <div className="ef-rij__sleepkaart">{nameOf(active)}</div>}
      </DragOverlay>
    </DndContext>
  )
}

const SILENT = {
  onDragStart: () => undefined,
  onDragMove: () => undefined,
  onDragOver: () => undefined,
  onDragEnd: () => undefined,
  onDragCancel: () => undefined,
}

/** Wat een rij van de sleeplaag krijgt: de ref voor de rij, de greep, en of hier de lijn staat. */
export interface RowHandle {
  readonly setRow: (element: HTMLElement | null) => void
  readonly grip: Record<string, unknown>
  readonly setGrip: (element: HTMLElement | null) => void
  readonly dragging: boolean
  /** De kant van de invoeglijn bij deze rij, als de gesleepte rij hier landt. */
  readonly line: Side | undefined
}

export function useRowHandle(rowId: RowId, order: readonly RowId[]): RowHandle {
  const { setNodeRef, setActivatorNodeRef, listeners, isDragging, active } = useSortable({
    id: rowId,
    // Geen glijden vanaf de oude plek: het kaartje stond al bij de pointer.
    animateLayoutChanges: () => false,
  })
  const { collisions } = useDndContext()
  const insertion = active === null ? undefined : insertionOf(collisions)
  const source = active === null ? undefined : (String(active.id) as RowId)
  const here = insertion !== undefined && insertion.id === rowId && source !== undefined && landingOf(order, source, insertion) !== null
  return {
    setRow: setNodeRef,
    grip: { ...listeners },
    setGrip: setActivatorNodeRef,
    dragging: isDragging,
    line: here ? insertion.kant : undefined,
  }
}

/** De invoeglijn: een stip en een streep in de ruimte boven of onder de rij, zonder zelf ruimte te nemen. */
export function RowInsertLine({ kant }: { readonly kant: Side }): ReactNode {
  return (
    <span className="ef-rij__invoeglijn" data-kant={kant} aria-hidden="true">
      <span className="ef-rij__invoegstip" />
      <span className="ef-rij__invoegstreep" />
    </span>
  )
}
